import {
  choice_types,
  textTypes,
} from "../Components/QuestionBuilder/questionTypes";
import {
  createResponseSkeleton,
  handleExternalVariables,
} from "../Components/Responses/utils";
import {
  ExternalResponsesDict,
  QuestionSlice,
} from "../Redux/reducers/responseSlice";
import { FormDataObject, FormDataRoot } from "../types/FormData.types";
import http from "./http";
import httpSurvey from "./httpSurvey";

type GetFormDataProps = {
  selectedForm: any;
  participantId?: string;
  studyId?: string;
  surveySlug?: string;
  surveyAssignmentId?: string;
};

export const getFormResponseData = async ({
  selectedForm,
  studyId,
  participantId,
  surveySlug,
  surveyAssignmentId,
}: GetFormDataProps) => {
  const formId = selectedForm?.id;
  let responseData = {} as FormDataObject;
  let params: any = { participantId };

  if (surveySlug && formId) {
    const { data } = await httpSurvey.get<any>(
      `/survey/${surveySlug}/${formId}`
    );

    responseData = data.data;
  } else if (surveyAssignmentId && formId && studyId) {
    params.surveyAssignmentId = surveyAssignmentId;
    const { data } = await http.get<FormDataRoot>(
      `/study/${studyId}/survey-responses/get/${formId}`,
      { params }
    );
    responseData = data.data;
  }

  // Combine Responses with Questions
  let questions = createResponseSkeleton(responseData);

  // Sort Questions
  questions = questions?.slice().sort((a, b) => a.position - b.position);

  const varnames: any = {};

  // Sort Choices
  questions = questions.map((q) => {
    varnames[q.varname] = 1;
    if (q.type === "summary" || q.properties.dynamicLabel) {
      const regex = /\{[A-Za-z][A-Za-z0-9._-]*\}/g;
      const matches = q.label.match(regex);
      if (matches) {
        q.summaryVars = Array.from(
          new Set([
            ...matches.map((val: string) => {
              const v = val.slice(1, -1);
              return v.includes(".") ? v.split(".")[0] : v;
            }),
          ])
        );
      }
    }
    if (q.choices && !q.properties?.randomizeChoices) {
      q.choices = q.choices.sort((a, b) => a.position - b.position);
    }
    return {
      ...q,
    };
  });

  const summaryQuestions = questions.filter(
    (q) => q.type === "summary" || q.properties.dynamicLabel
  );

  questions = questions.map((question) => {
    summaryQuestions.forEach((q2) => {
      if (q2.summaryVars?.includes(question.varname)) {
        if (question.summaryOperands) {
          question.summaryOperands.push(q2.id);
        } else {
          question.summaryOperands = [q2.id];
        }
      }
    });

    return {
      ...question,
    };
  });

  let summaryExternalVars: string[] = [];

  summaryQuestions.forEach((q) => {
    if (q.summaryVars) {
      q.summaryVars.forEach((v) => {
        if (!varnames[v]) {
          summaryExternalVars.push(v);
        }
      });
    }
  });

  summaryExternalVars = Array.from(new Set([...summaryExternalVars]));

  let summaryExternalResponses = {} as ExternalResponsesDict;
  if (summaryExternalVars.length > 0) {
    summaryExternalResponses = await handleExternalVariables(
      summaryExternalVars,
      participantId,
      surveySlug,
      studyId,
      formId,
      "",
      surveyAssignmentId,
      ""
    );
    Object.keys(summaryExternalResponses).forEach((varname) => {
      summaryQuestions.forEach((q) => {
        if (q.summaryVars?.includes(varname)) {
          const currObject = summaryExternalResponses?.[varname];

          if (
            currObject?.calculationOperands &&
            currObject.calculationOperands.length > 0
          ) {
            currObject.calculationOperands.push(q.id);
          } else {
            currObject.calculationOperands = [q.id];
          }
        }
      });
    });
  }

  const summaryIds = summaryQuestions.map((q) => q.id);
  if (summaryIds.length > 0) {
    handleSummaries(summaryIds, questions, summaryExternalResponses, "");
  }

  return {
    id: responseData?.form?.id,
    formName: responseData?.form?.name,
    questions,
  };
};

const handleSummaries = async (
  ids: string[],
  questionList: QuestionSlice[],
  summaryExternalResponses: any,
  lang?: string
) => {
  try {
    const updatedIds = [...new Set(ids)];
    for (let i = 0; i < updatedIds.length; i++) {
      const summaryField = questionList.find((q) => q.id === updatedIds[i]);
      if (summaryField?.labelTemplate) {
        let template = summaryField.labelTemplate;
        let variableNames: string[] = summaryField.summaryVars || [];
        let values: any[] = [];
        const translation: Record<string, any> = {};
        if (lang) {
          summaryField?.summaryMap?.forEach?.((item: any) => {
            translation[item.key] = item.value;
          });
        }

        let tableVars: Array<{ external: boolean; varname: string }> = [];

        // Get Value of all varNames
        for (const varName of variableNames) {
          const currentQues = questionList.find((q) => q.varname === varName);
          let notFound = false;
          if (!currentQues) {
            const currentExtQues = summaryExternalResponses[varName];

            if (currentExtQues) {
              let response: any = "";
              if (textTypes.includes(currentExtQues.type)) {
                response = currentExtQues?.textValue ?? "";
              } else if (choice_types.includes(currentExtQues.type)) {
                response = currentExtQues?.choiceLabel ?? "";
              } else if (
                ["grid", "repeated_data"].includes(currentExtQues.type)
              ) {
                tableVars.push({
                  external: true,
                  varname: varName,
                });
                response = null;
              } else if (currentExtQues.type === "calculated_field") {
                response =
                  currentExtQues?.textValue ??
                  currentExtQues?.numberValue ??
                  "";
              } else {
                response = currentExtQues?.numberValue ?? "";
              }
              values.push(response);
            } else {
              if (!template.includes(`{${varName}}`)) {
                tableVars.push({
                  external: true,
                  varname: varName,
                });
                values.push(null);
              } else {
                notFound = true;
              }
            }
          } else if (currentQues.type === "checkbox") {
            if (currentQues.responsesBackup.length === 0) {
              notFound = true;
            } else {
              let text = "";
              for (let i = 0; i < currentQues.responsesBackup.length; i++) {
                const res = currentQues.responsesBackup?.[i];
                if (!res?.questionChoice?.label) {
                  notFound = true;
                } else {
                  text +=
                    res?.questionChoice?.label +
                    (res?.textValue ? " - " + res.textValue : "") +
                    ", ";
                }
              }
              if (text) {
                text = text.slice(0, -2);
              }

              if (!notFound) {
                values.push(text);
              }
            }
          } else if (
            currentQues.type === "grid" ||
            currentQues.type === "repeated_data"
          ) {
            tableVars.push({
              external: false,
              varname: varName,
            });
            values.push(null);
          } else {
            let response = "";
            if (textTypes.includes(currentQues.type)) {
              response = currentQues?.responsesBackup?.[0]?.textValue ?? "";
            } else if (choice_types.includes(currentQues.type)) {
              if (currentQues?.responsesBackup?.[0]?.questionChoice?.label) {
                response =
                  currentQues?.responsesBackup?.[0]?.questionChoice?.label +
                    (currentQues?.responsesBackup?.[0]?.textValue
                      ? " - " + currentQues?.responsesBackup?.[0]?.textValue
                      : "") || "";
              }
            } else if (currentQues.type === "calculated_field") {
              if (currentQues?.properties?.responseType === "string") {
                response = currentQues?.responsesBackup?.[0]?.textValue ?? "";
              } else {
                response = currentQues?.responsesBackup?.[0]?.numberValue ?? "";
              }
            } else {
              response = currentQues?.responsesBackup?.[0]?.numberValue ?? "";
            }
            values.push(response);
          }
          if (notFound) {
            values.push("");
          }
        }

        // Replace template varNames with values
        variableNames.forEach((varName, index) => {
          if (values[index] !== null) {
            const re = new RegExp(`{${varName}}`, "g");
            if (lang && translation[values[index]]) {
              template = template.replace(re, translation[values[index]]);
            } else {
              template = template.replace(re, values[index]);
            }
          }
        });

        if (tableVars.length > 0) {
          for (let i = 0; i < tableVars.length; i++) {
            const actualVar = tableVars[i].varname;
            const regex = new RegExp(`{${actualVar}.[a-z0-9]*.[0-9]*}`, "g");
            let matches = template.match(regex);
            const matchesArr = matches
              ? Array.from(new Set([...matches.map((v) => v.slice(1, -1))]))
              : null;
            if (matchesArr) {
              for (let j = 0; j < matchesArr.length; j++) {
                const match: string = matchesArr[j];
                const splitStr = match.split(".");
                const aggregate =
                  splitStr[1] === "row" || splitStr[1] === "column";
                const row = aggregate ? splitStr[1] : +splitStr[1];
                const column = +splitStr[2];
                const varname = splitStr[0];
                let grid = null;
                if (tableVars?.[i].external) {
                  if (aggregate) {
                    grid = summaryExternalResponses?.[varname]?.aggregateData;
                  } else {
                    grid = summaryExternalResponses?.[varname]?.gridValue;
                  }
                } else {
                  const currentQues = questionList.find(
                    (q) => q.varname === varname
                  );
                  if (aggregate) {
                    grid = currentQues?.responsesBackup?.[0]?.aggregateData;
                  } else {
                    grid =
                      currentQues?.responsesBackup?.[0]?.[
                        currentQues.type === "grid"
                          ? "gridData"
                          : "repeatedData"
                      ];
                  }
                }
                const response =
                  grid?.[row]?.[column]?.textValue ??
                  grid?.[row]?.[column]?.numberValue ??
                  "";
                const re = new RegExp(`{${match}}`, "g");
                template = template.replace(re, response);
              }
            }
          }
        }

        if (summaryField.label !== template) {
          summaryField.label = template;
        }
      }
    }
  } catch (err) {
    console.log("error occured");
    console.log(err);
  }
};
